/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { TableManager } from './table-manager';
import { Observable, Subject } from 'rxjs';
import { DataModel } from '../../engine/data-model';
import { HttpFilter } from '../../engine/http/http-filter';
import { NameValue } from '../../tool/name-value';
import { ColumnManager } from './column-manager';
import { Operator } from '../../enum/operator.enum';
import { ModelProperty } from '../../engine/model-property';
import { IEntity } from '../../interface/IEntity';
import { RequestFactory } from './request-factory';
import { RestService } from '../../engine/http/rest.service';
import { GroupEntity } from '../../tool/group-entity';
import { PropertyType } from '../../engine/property-type.enum';
import { ObjectFactory } from '../../engine/http/factory/object-factory';
import { StandardEntity } from '../../engine/standard-entity';

/**
 * Manager load table step by step
 */
export class PageTableManager<T extends IEntity> extends TableManager<T> {
  public override loadSubject: Subject<T[]> = new Subject<T[]>();
  public override isFlowView = false;
  public paging: number;
  public start: number;
  public filterTimeOut = null;
  public request: RequestFactory<T>;
  public urlParams: HttpFilter = new HttpFilter();
  public sumToLoad = false;
  public isFirstLoad = true;

  constructor(
    public restService: RestService,
    model: DataModel,
    request: RequestFactory<T>,
    paging?: number,
    start?: number,
  ) {
    super(model);
    this.paging = paging ? paging : 50;
    this.start = start ? start : 1;
    this.fullLoad = false;
    this.request = request;
    this.currentPage = 0;
    this._printableData = [];
  }

  protected createRequest$(limit: number, start: number) {
    const filters = new HttpFilter([...this.request.filter.getFilters()]);
    filters.setFilters('LIMIT', limit);
    filters.setFilters('START', start);
    filters.concat(this.urlParams);
    return this.restService.getAllWithCount(this.request.factory, {
      httpFilter: filters,
      showLoading: true,
    });
  }

  public getEntities(): Observable<T[]> {
    return this.loadSubject.asObservable();
  }

  public load(forceRefresh: boolean): void {
    void forceRefresh;
    this.isLoading.emit(true);
    this.createRequest$(this.paging, this.start).subscribe({
      next: (result) => {
        this.count = result.count;
        this.data = result.data;
        this._printableData = result.data;
        this.loadSubject.next(result.data);
      },
      complete: () => {
        this.isLoading.emit(false);
      },
    });
  }

  private getUrl(api: string): string {
    if (this.request.factory.getApi().indexOf('/') === -1) {
      return `${api}/${this.request.factory.getApi()}`;
    } else {
      const apiSplit = this.request.factory.getApi().split('/');
      return `${apiSplit[0]}/${api}/${apiSplit[1]}`;
    }
  }

  public loadGroups$(column: ColumnManager): Observable<T[]> {
    const group =
      column.property.type === PropertyType.object
        ? column.property.id + '.' + column.property.wsKey
        : column.property.id;
    const url = this.getUrl('groups') + `/${group}`;
    const filter = new HttpFilter(
      this.request.filter.getFilters().concat(this.urlParams.getFilters()),
    );
    return this.restService.getAll(new ObjectFactory(url), {
      httpFilter: filter,
    });
  }

  public loadTotal() {
    this.totalLoaded = false;
    const columnGrouped = this.columns.find((column) => column.isGrouped);
    const filter = this.request.filter
      .getFilters()
      .concat(this.urlParams.getFilters());
    if (columnGrouped) {
      filter.push(new NameValue('groupBy', columnGrouped.property.id));
    }
    const httpFilter = new HttpFilter(filter);
    this.restService
      .getAll(new ObjectFactory(this.getUrl('total')), {
        httpFilter: httpFilter,
        showLoading: false,
      })
      .subscribe((totals) => {
        this.buildTotals(totals);
        this.totalLoaded = true;
      });
  }

  private buildTotals(totals: StandardEntity[]) {
    this.rowTotal = new StandardEntity() as any;
    for (const total of totals) {
      const fieldName = total['fieldname'];
      this.rowTotal[fieldName] = total['value'];
      if (total['detailsByGroup']) {
        for (const detail of total['detailsByGroup']) {
          const group = this.groupEntities.find(
            (gr) => gr.value.split(' - ')[0] === detail.id,
          );
          if (group) {
            group.total[fieldName] = detail['value'];
          }
        }
      }
    }
  }

  public openGroups$(
    groups: GroupEntity<T>[],
    column: ColumnManager,
  ): Observable<T[]> {
    if (this.sorter) {
      let fieldName = `${this.sorter.column.id}`;
      if (this.sorter.indice === -1) {
        fieldName = '-' + fieldName;
      }
      if (this.sorter.column.getPrimaryKey()) {
        fieldName += `.${this.sorter.column.getPrimaryKey()}`;
      }
      this.urlParams.setFilters('by', fieldName);
    }
    const filters = new HttpFilter([
      ...this.request.filter.getFilters().concat(this.urlParams.getFilters()),
    ]);
    filters.setFilters('LIMIT', 0);
    const field =
      column.property.type === PropertyType.object
        ? column.property.id + '.' + column.property.wsKey
        : column.property.id;
    filters.setFilters(
      'WITH',
      field + '|=|' + groups.map((group) => group.value.split(' - ')[0]),
      false,
    );
    return this.restService.getAll(this.request.factory, {
      httpFilter: filters,
    });
  }

  public changePage(page: number, step: number) {
    this.currentPage = page;
    this.start = this.currentPage * step + 1;
    this.load(false);
  }

  public changeStep(previousValue: number, newValue: number) {
    this.isLoading.emit(false);
    this.currentPage = 0;
    this.paging = newValue;
    this.start = 0;
    this.createRequest$(this.paging, this.start).subscribe({
      next: (result) => {
        this.count = result.count;
        this.data = result.data;
        this._printableData = result.data;
        this.loadSubject.next(result.data);
        this.isLoading.emit(false);
      },
    });
  }

  public override filter(column: ColumnManager, filters: NameValue[]) {
    this.setFilter(column, filters);
    this.currentPage = 0;
    this.urlParams = this.generateUrlFilters();
    this.urlParams.getFilters().forEach((filter) => {
      if (filter.value.includes('[""]')) {
        filter.value = filter.value.replace('[""]', '""');
      }
    });
    this.isLoading.emit(true);
    if (this.filterTimeOut) {
      clearTimeout(this.filterTimeOut);
    }

    this.filterTimeOut = setTimeout(() => {
      this.start = 0;
      const columnGrouped = this.columns.find((column) => column.isGrouped);
      if (columnGrouped) {
        this.changeGroup.next({ column: columnGrouped, open: false });
        this.isLoading.emit(false);
      } else {
        this.notify();
      }
    }, 750);
  }

  protected generateUrlFilters(): HttpFilter {
    const filters = new HttpFilter();
    for (const f of this.state.filterProperties) {
      for (const value of f.filters) {
        filters.setFilters(
          'WITH',
          this.buildUrlProperty(f.property, value),
          false,
        );
      }
    }
    return filters;
  }

  protected buildUrlProperty(p: ModelProperty, filter: NameValue): string {
    let result = null;
    const field = p.id;
    // p.getLabelKey() && p.getLabelKey().length > 0
    //   ? `${p.id}.${p.getLabelKey()[0]}`
    //   : p.id;
    switch (filter.name) {
      case Operator.contain:
        result = `${field}|=|[${filter.value}]`;
        break;
      case Operator.equal:
        result = `${field}|=|${filter.value}`;
        break;
      case Operator.greater:
        result = `${field}|>|${filter.value}`;
        break;
      case Operator.greaterOrEqual:
        result = `${field}|>=|${filter.value}`;
        break;
      case Operator.less:
        result = `${field}|<|${filter.value}`;
        break;
      case Operator.lessOrEqual:
        result = `${field}|<=|${filter.value}`;
        break;
    }
    return result;
  }

  public override hasCompleteData(): boolean {
    return false;
  }

  public override getPrintableData(): Observable<T[]> {
    const filter = new HttpFilter();
    filter.concat(this.request.filter);
    filter.concat(this.urlParams);
    return this.restService.getAll(this.request.factory, {
      httpFilter: this.urlParams,
    });
  }

  public override sort(column: ModelProperty, index: 1 | -1) {
    this.reset();
    let fieldName = `${column.id}`;
    if (index === -1) {
      fieldName = '-' + fieldName;
    }
    if (column.getPrimaryKey()) {
      fieldName += `.${column.getPrimaryKey()}`;
    }
    this.urlParams.setFilters('by', fieldName);
    if (!this.count) {
      return;
    }
    if (this.isFirstLoad) {
      return;
    }
    this.isLoading.emit(true);
    this.notify(true);
  }

  sortGroups(column: ModelProperty, index: 1 | -1, groups: GroupEntity<T>[]) {
    for (const group of groups.filter((gr) => gr.open)) {
      group.items.sort((a, b) => {
        return super.compare(a.entity, b.entity, column) * index;
      });
    }
  }

  public override notify(forceRefresh?: boolean) {
    super.notify(forceRefresh);
    if (this.sumToLoad) {
      this.loadTotal();
    }
  }

  public notifySort(data: any[]) {
    void data;
    return;
  }

  private reset(): void {
    this.start = 0;
    this.currentPage = 0;
  }
}
